import { useCallback, useEffect, useState } from "react";
import { Bike, CheckCircle2, ImageIcon, Loader2, RefreshCw, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState } from "@/components/common";
import { RideStatus, RoutePoints } from "@/components/boy/RideStatus";
import { api, brl, errMsg, fmtDateTime } from "@/lib/api";
import { fmtKm } from "@/lib/boy";
import { BoyTrackButton } from "@/components/boy/BoyLive";
import { cn } from "@/lib/utils";

const FILTERS = [["todas", "Todas"], ["aguardando", "Aguardando"], ["andamento", "Em andamento"], ["entregue", "Entregues"], ["cancelada", "Canceladas"]];

function PhotoButton({ id }) {
  const [url, setUrl] = useState("");
  const open = async () => {
    try {
      const { data } = await api.get(`/boy/rides/${id}/photo`, { responseType: "blob" });
      setUrl(URL.createObjectURL(data));
    } catch {
      toast.error("Não foi possível abrir a foto");
    }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={open} data-testid={`boy-panel-photo-${id}`}><ImageIcon className="mr-1.5 h-4 w-4" />Foto</Button>
      <Dialog open={!!url} onOpenChange={(o) => !o && setUrl("")}>
        <DialogContent><DialogHeader><DialogTitle>Foto do pedido</DialogTitle></DialogHeader><img src={url} alt="Foto do pedido" className="max-h-[70vh] w-full rounded-lg object-contain" /></DialogContent>
      </Dialog>
    </>
  );
}

const Val = ({ k, v, strong }) => <div><dt className="text-[11px] text-muted-foreground">{k}</dt><dd className={cn("truncate", strong ? "font-bold" : "font-medium")}>{v}</dd></div>;

function RideRow({ r, busy, act }) {
  const paid = r.payment.status === "pago";
  const d = r.dispatch || {};
  const b = (path) => busy === r.id + path;
  const icon = (path, Icon) => (b(path) ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Icon className="mr-1.5 h-4 w-4" />);
  return (
    <div className="card-surface grid gap-4 p-4 lg:grid-cols-[1fr_auto]" data-testid={`boy-panel-ride-${r.number}`}>
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2"><span className="font-bold">#{r.number}</span><RideStatus r={r} testId={`boy-panel-ride-status-${r.number}`} /><span className="text-xs text-muted-foreground">{fmtDateTime(r.created_at)} · {r.merchant.name}</span></div>
        <RoutePoints from={r.origin.label} to={r.dest.label} />
        {r.status === "aguardando" && d.status === "no_courier" && <p className="text-xs font-medium text-red-600 dark:text-red-400" data-testid={`boy-panel-ride-reason-${r.number}`}>{d.reason}</p>}
        {r.status === "aguardando" && d.status === "offering" && <p className="text-xs text-muted-foreground">Oferecida a {d.offered.length} motoboy(s) · envio {d.round + 1} de 4, reenvia a cada 5 min</p>}
      </div>
      <div className="flex flex-col gap-3 lg:min-w-64 lg:items-end lg:text-right">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4 lg:grid-cols-2">
          <Val k={`Valor · ${fmtKm(r.distance_km)} km`} v={brl(r.price)} strong />
          <Val k={`Comissão ${r.commission_pct}%`} v={brl(r.commission)} />
          <Val k="Motoboy recebe" v={brl(r.courier_value)} />
          <Val k="Motoboy" v={r.courier ? r.courier.name : "—"} />
        </dl>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {r.status === "aguardando" && !paid && <Button size="sm" variant="outline" disabled={b("check-payment")} onClick={() => act(r, "check-payment")} data-testid={`boy-panel-check-${r.number}`}>{icon("check-payment", RefreshCw)}Conferir pagamento</Button>}
          {r.status === "aguardando" && paid && d.status !== "offering" && <Button size="sm" disabled={b("redispatch")} onClick={() => act(r, "redispatch", "Motoboys chamados de novo")} data-testid={`boy-panel-redispatch-${r.number}`}>{icon("redispatch", Send)}Chamar motoboys</Button>}
          {r.status === "andamento" && r.track_token && <BoyTrackButton token={r.track_token} number={r.number} testId={`boy-panel-map-${r.number}`} />}
          {r.status === "andamento" && <Button size="sm" disabled={b("deliver")} onClick={() => act(r, "deliver", "Corrida marcada como entregue")} data-testid={`boy-panel-deliver-${r.number}`}>{icon("deliver", CheckCircle2)}Marcar entregue</Button>}
          {r.has_photo && <PhotoButton id={r.id} />}
          {!["entregue", "cancelada"].includes(r.status) && <Button size="sm" variant="ghost" className="text-red-600" disabled={b("cancel")} onClick={() => window.confirm(`Cancelar a corrida #${r.number}?`) && act(r, "cancel", "Corrida cancelada")} data-testid={`boy-panel-cancel-${r.number}`}>{icon("cancel", X)}Cancelar</Button>}
        </div>
      </div>
    </div>
  );
}

export function RidesTab() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("todas");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/boy/rides").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);
  const act = async (r, path, ok) => {
    setBusy(r.id + path);
    try {
      const { data } = await api.post(`/boy/rides/${r.id}/${path}`);
      if (path === "check-payment") toast[data.payment.status === "pago" ? "success" : "message"](data.payment.status === "pago" ? "Pagamento confirmado" : "Pagamento ainda não confirmado");
      else toast.success(ok);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  if (!rows) return <p className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></p>;
  const count = (k) => (k === "todas" ? rows.length : rows.filter((r) => r.status === k).length);
  const term = q.trim().toLowerCase();
  const list = rows.filter((r) => (filter === "todas" || r.status === filter) && (!term || `${r.number} ${r.merchant.name} ${r.courier?.name || ""}`.toLowerCase().includes(term)));
  return (
    <div className="space-y-4" data-testid="boy-rides-tab">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`boy-filter-${k}`}
            className={cn("rounded-full border px-3 py-1.5 text-sm transition-colors", filter === k ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent")}>
            {l}<span className="ml-1.5 font-mono text-xs opacity-70">{count(k)}</span>
          </button>
        ))}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por número, lojista ou motoboy" className="sm:ml-auto sm:w-72" data-testid="boy-rides-search" />
      </div>
      {!list.length ? <EmptyState icon={Bike} title="Nenhuma corrida" text="As corridas pedidas pelos lojistas aparecem aqui." />
        : <div className="grid gap-3">{list.map((r) => <RideRow key={r.id} r={r} busy={busy} act={act} />)}</div>}
    </div>
  );
}
